import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/components/AuthProvider';

interface NavigationProps {
  onSignInClick: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ onSignInClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, loading } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { label: 'Home', href: '/' },
    { 
      label: 'Learn', 
      href: '/guide',
      dropdown: [
        { label: 'How It Works', href: '/how-it-works' },
        { label: 'Life Insurance Guide', href: '/guide' },
        { label: 'Insurance Calculator', href: '/calculator' },
        { label: 'Blog', href: '/blog' },
        { label: 'FAQ', href: '/faq' },
        { label: 'About Safurra', href: '/about' },
      ]
    },
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center space-x-8">
        {navItems.map((item) => (
          <div key={item.label} className="relative">
            {item.dropdown ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center space-x-1 text-foreground hover:text-primary transition-colors"
                  >
                    <span className="font-medium">{item.label}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="start" 
                  className="w-56 bg-background border border-border shadow-lg z-50"
                >
                  {item.dropdown.map((subItem) => (
                    <DropdownMenuItem key={subItem.href} asChild>
                      <Link 
                        to={subItem.href}
                        className="flex w-full px-4 py-3 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                      >
                        {subItem.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link 
                to={item.href}
                className={cn(
                  "font-medium text-foreground hover:text-primary transition-colors px-3 py-2 rounded-md",
                  isActive(item.href) && "text-primary bg-primary/5"
                )}
              >
                {item.label}
              </Link>
            )}
          </div>
        ))}
      </nav>

      {/* Auth Buttons - Desktop */}
      <div className="hidden md:block">
        {loading ? (
          <div className="h-9 w-20 animate-pulse bg-muted rounded"></div>
        ) : user ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user.email}</span>
            <Button variant="outline" asChild>
              <Link to="/dashboard">Dashboard</Link>
            </Button>
          </div>
        ) : (
          <Button variant="outline" asChild className="font-medium border-primary/20 hover:border-primary hover:bg-primary/5">
            <Link to="/signin">Sign In</Link>
          </Button>
        )}
      </div>

      {/* Mobile Menu Button */}
      <div className="md:hidden flex items-center space-x-2">
        {loading ? (
          <div className="h-8 w-16 animate-pulse bg-muted rounded"></div>
        ) : user ? (
          <Button variant="outline" size="sm" asChild>
            <Link to="/dashboard">Dashboard</Link>
          </Button>
        ) : (
          <Button variant="outline" size="sm" asChild className="font-medium">
            <Link to="/signin">Sign In</Link>
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2"
          aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          {isMobileMenuOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </Button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-background border-b border-border shadow-lg z-50 md:hidden">
          <div className="container mx-auto px-6 py-4 space-y-4">
            {navItems.map((item) => (
              <div key={item.label}>
                {item.dropdown ? (
                  <div className="space-y-2">
                    <div className="font-medium text-foreground py-2">
                      {item.label}
                    </div>
                    <div className="pl-4 space-y-2">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.href}
                          to={subItem.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block text-muted-foreground hover:text-primary transition-colors py-2"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "block font-medium text-foreground hover:text-primary transition-colors py-2",
                      isActive(item.href) && "text-primary"
                    )}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};